import { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChatBubbleBottomCenterTextIcon, ClipboardDocumentCheckIcon, InboxIcon } from '@heroicons/react/20/solid';
import { Worksheet } from '../../../types/worksheet';
import { BadgeV3 } from '@cimplico/components';
import clsx from 'clsx';
import { getOutstandingConditionFigures } from '../utils';

type SidebarActionsProps = {
    worksheet: Worksheet;
};
function SidebarActions({ worksheet }: SidebarActionsProps): JSX.Element {
    const { comments, queries, reviewConditions } = getOutstandingConditionFigures(worksheet);
    return (
        <div className="flex items-center" data-intercom-target="worksheet-communication-tab-buttons">
            <SideAction id="internalComments" icon={<ChatBubbleBottomCenterTextIcon />} figure={comments} />
            <SideAction id="clientQueries" icon={<InboxIcon />} figure={queries} />
            <SideAction
                id="reviewConditions"
                icon={<ClipboardDocumentCheckIcon />}
                figure={reviewConditions}
                dataIntercomTarget="worksheet-review-conditions-button"
            />
        </div>
    );
}

type TopActionProps = {
    figure: number;
    id: string;
    icon: ReactNode;
    dataIntercomTarget?: string;
};

function SideAction({ figure, id, icon, dataIntercomTarget }: TopActionProps): JSX.Element {
    const [search, setSearch] = useSearchParams();

    const isActive = search.get('addon') === id;

    function toggleAddon(id: string): void {
        setSearch(
            (s) => {
                if (search.get('addon') === id) {
                    s.delete('addon');
                } else {
                    s.set('addon', id);
                }
                return s;
            },
            { replace: true }
        );
    }
    return (
        <button
            className="rounded-md flex px-2 py-1 items-center gap-2 hover:bg-gray-50 active:bg-gray-50"
            onClick={(): void => {
                toggleAddon(id);
            }}
            {...(dataIntercomTarget ? { 'data-intercom-target': dataIntercomTarget } : {})}
        >
            <BadgeV3 rounded size="small" label={figure.toString()} color={isActive ? 'indigo' : 'gray'} />
            <span className={clsx('shrink-0 w-5 h-5', isActive ? 'text-indigo-600' : 'text-gray-600')}>{icon}</span>
        </button>
    );
}

export default SidebarActions;
